import { useCallback, useState } from "react";
import { createViewSegmentValues } from "@/modules/rev_eu/master_data/views/ViewSegmentValues";
import type {
    TCustomQuery,
    TMasterQuery,
    TRulesQuery,
    TSegmentationMappingRow,
    TSegmentCustomValueRow,
    TSegmentValueRow,
} from "@/modules/rev_eu/master_data/views/ViewSegmentValues";
import type { TSegmentationRuleDraft } from "@/modules/rev_eu/master_data/views/ViewSegmentValues/SegmentationRuleFormModal";
import { NOOP } from "@/utils/utils";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";

type TPageState<T> = {
    items: T[];
    total: number;
    totalUnfiltered: number;
    isLoading: boolean;
    hasError: boolean;
};

function emptyPage<T>(): TPageState<T> {
    return { items: [], total: 0, totalUnfiltered: 0, isLoading: true, hasError: false };
}

export const wireSettingsSegmentValues = ({
    store: _store,
    useAppStore,
    api,
}: TCompositionDeps) => {
    const ViewSegmentValues = createViewSegmentValues({
        useCases: () => {
            const userPermissions = useAppStore((state) => state.user?.permissions ?? []);
            const [master, setMaster] = useState<TPageState<TSegmentValueRow>>(emptyPage);
            const [custom, setCustom] = useState<TPageState<TSegmentCustomValueRow>>(emptyPage);
            const [rules, setRules] = useState<TPageState<TSegmentationMappingRow>>(emptyPage);

            const onLoadMasterPage = useCallback(
                async (segmentNumber: number, query: TMasterQuery, signal?: AbortSignal) => {
                    setMaster((p) => ({ ...p, isLoading: true, hasError: false }));
                    try {
                        const data = await api.segmentValues.list(
                            segmentNumber,
                            {
                                ...query,
                                active_only: false,
                            },
                            signal,
                        );
                        setMaster({
                            items: data.items.map((item) => ({
                                id: item.id,
                                segmentNumber: item.segment_number,
                                code: item.code,
                                description: item.description,
                                yardiSegmentValue: item.yardi_segment_value,
                                isActive: item.is_active,
                            })),
                            total: data.total,
                            totalUnfiltered: data.total_unfiltered ?? data.total,
                            isLoading: false,
                            hasError: false,
                        });
                    } catch (err) {
                        // Swallow abort errors — the superseding request owns the loading state.
                        if (err instanceof DOMException && err.name === "AbortError") return;
                        setMaster((p) => ({ ...p, isLoading: false, hasError: true }));
                    }
                },
                [api],
            );

            const onLoadCustomPage = useCallback(
                async (segmentType: number, query: TCustomQuery, signal?: AbortSignal) => {
                    setCustom((p) => ({ ...p, isLoading: true, hasError: false }));
                    try {
                        const data = await api.segmentCustomValues.list(
                            {
                                ...query,
                                segment_type: segmentType,
                                active_only: false,
                            },
                            signal,
                        );
                        setCustom({
                            items: data.items.map((item) => ({
                                id: item.id,
                                accountCode: item.account_code,
                                segmentType: item.segment_type,
                                code: item.code,
                                description: item.description ?? null,
                                propertyCode: item.property_code,
                                isActive: item.is_active,
                            })),
                            total: data.total,
                            totalUnfiltered: data.total_unfiltered ?? data.total,
                            isLoading: false,
                            hasError: false,
                        });
                    } catch (err) {
                        // Swallow abort errors — the superseding request owns the loading state.
                        if (err instanceof DOMException && err.name === "AbortError") return;
                        setCustom((p) => ({ ...p, isLoading: false, hasError: true }));
                    }
                },
                [api],
            );

            const onLoadRulesPage = useCallback(
                async (query: TRulesQuery, signal?: AbortSignal) => {
                    setRules((p) => ({ ...p, isLoading: true, hasError: false }));
                    try {
                        const data = await api.segmentationMappings.list(
                            {
                                ...query,
                                active_only: false,
                            },
                            signal,
                        );
                        setRules({
                            items: data.items.map((item) => ({
                                id: item.id,
                                accountCode: item.account_code,
                                isMandatorySegment2: item.is_mandatory_segment_2,
                                isMandatorySegment4: item.is_mandatory_segment_4,
                                isMandatorySegment5: item.is_mandatory_segment_5,
                                isMandatorySegment6: item.is_mandatory_segment_6,
                                isMandatorySegment7: item.is_mandatory_segment_7,
                                hasCustomSegment2: item.has_custom_segment_2,
                                hasCustomSegment5: item.has_custom_segment_5,
                                hasCustomSegment6: item.has_custom_segment_6,
                                hasCustomSegment7: item.has_custom_segment_7,
                                isActive: item.is_active,
                            })),
                            total: data.total,
                            totalUnfiltered: data.total_unfiltered ?? data.total,
                            isLoading: false,
                            hasError: false,
                        });
                    } catch (err) {
                        // Swallow abort errors — the superseding request owns the loading state.
                        if (err instanceof DOMException && err.name === "AbortError") return;
                        setRules((p) => ({ ...p, isLoading: false, hasError: true }));
                    }
                },
                [api],
            );

            return {
                onMount: NOOP,
                onUnmount: NOOP,
                permissions: () => userPermissions,
                onLoadMasterPage,
                useMasterPage: () => master,
                onLoadCustomPage,
                useCustomPage: () => custom,
                onLoadRulesPage,
                useRulesPage: () => rules,
                onCreateSegmentValue: useCallback(
                    async (data: {
                        segment_number: number;
                        code: string;
                        description?: string;
                        yardi_segment_value?: string;
                        is_active?: boolean;
                    }) => {
                        await api.segmentValues.create(data);
                    },
                    [api],
                ),
                onUpdateSegmentValue: useCallback(
                    async (
                        id: string,
                        data: {
                            code?: string;
                            description?: string;
                            yardi_segment_value?: string;
                            is_active?: boolean;
                        },
                    ) => {
                        await api.segmentValues.update(id, data);
                    },
                    [api],
                ),
                onDeleteSegmentValue: useCallback(
                    async (id: string) => {
                        await api.segmentValues.delete(id);
                    },
                    [api],
                ),
                onCreateSegmentCustomValue: useCallback(
                    async (data: {
                        account_code: string;
                        segment_type: number;
                        code: string;
                        description: string;
                        property_code: string | null;
                        is_active: boolean;
                    }) => {
                        await api.segmentCustomValues.create(data);
                    },
                    [api],
                ),
                onUpdateSegmentCustomValue: useCallback(
                    async (
                        id: string,
                        data: { code?: string; description?: string; is_active?: boolean },
                    ) => {
                        await api.segmentCustomValues.update(id, data);
                    },
                    [api],
                ),
                onDeleteSegmentCustomValue: useCallback(
                    async (id: string) => {
                        await api.segmentCustomValues.delete(id);
                    },
                    [api],
                ),
                onCreateSegmentationMapping: useCallback(
                    async (data: TSegmentationRuleDraft) => {
                        await api.segmentationMappings.create(data);
                    },
                    [api],
                ),
                onUpdateSegmentationMapping: useCallback(
                    async (id: string, data: Partial<TSegmentationRuleDraft>) => {
                        await api.segmentationMappings.update(id, data);
                    },
                    [api],
                ),
                onDeleteSegmentationMapping: useCallback(
                    async (id: string) => {
                        await api.segmentationMappings.delete(id);
                    },
                    [api],
                ),
                onLoadActiveMasterValues: useCallback(
                    async (
                        segmentNumber: number,
                    ): Promise<Array<{ code: string; description: string }>> => {
                        const data = await api.segmentValues.list(segmentNumber, {
                            active_only: true,
                            page_size: 1000,
                        });
                        return data.items.map((item) => ({
                            code: item.code,
                            description: item.description ?? "",
                        }));
                    },
                    [api],
                ),
            };
        },
    });

    return { ViewSegmentValues };
};
